import { useState, useEffect, useMemo, useCallback } from 'react';

import moment from 'moment';

import { translateMonthAndYear } from 'utils/functions';
import { useMediaQuery } from 'react-responsive';
import { getConsumptionDetails, getConsumptionHistory } from '../services';
import {
  ConsumptionHistoryData,
  DataFormated,
  GetConsumptionDetailsData,
  TypeReport,
} from '../types';

type UseConsumptionHistoryReturn = {
  consumptionDetailsData: GetConsumptionDetailsData[];
  consumptionDataFormated: DataFormated[];
  isLoading: boolean;
  isLoadingExcel: boolean;
  average: string | number;
  maxValue: number;
  exportExcel: () => void;
};

const useConsumptionHistory = (
  dataValue: number,
  typeReport?: TypeReport
): UseConsumptionHistoryReturn => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingExcel, setIsLoadingExcel] = useState<boolean>(false);
  const [consumptionData, setConsumptionData] = useState<
    ConsumptionHistoryData[]
  >([]);
  const [consumptionDetailsData, setConsumptioDetailsData] = useState<
    GetConsumptionDetailsData[]
  >([]);
  const [monthType, setMonthType] = useState<boolean>(false);
  const [initialDate, setInitialDate] = useState(() => {
    const currentDate = new Date();
    const startDateSelected = new Date(
      currentDate.setMonth(currentDate.getMonth() - 1)
    );
    return moment(startDateSelected).format('YYYY-MM-DD');
  });
  const [finalDate, setFinalDate] = useState(
    moment(new Date()).format('YYYY-MM-DD')
  );

  const isMobile = useMediaQuery({ maxWidth: 768 });

  useEffect(() => {
    setMonthType(false);
    const currentDate = new Date();
    const startDateSelected = new Date(
      currentDate.setMonth(currentDate.getMonth() - dataValue)
    );

    const endDateSelected = new Date();
    setInitialDate(moment(startDateSelected).format('YYYY-MM-DD'));
    setFinalDate(moment(endDateSelected).format('YYYY-MM-DD'));
    setMonthType(dataValue > 1);
  }, [dataValue]);

  const exportExcel = useCallback(async () => {
    setIsLoadingExcel(true);
    await getConsumptionHistory({
      currentPage: '0',
      finalDate,
      initialDate,
      monthType,
      exportExcel: true,
    });
    setIsLoadingExcel(false);
  }, [initialDate, finalDate, monthType]);

  const getGraphicData = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    const response = await getConsumptionHistory({
      currentPage: '0',
      finalDate,
      initialDate,
      monthType,
    });
    setConsumptionData(response);
    setIsLoading(false);
  }, [initialDate, finalDate, monthType]);

  const getGraphicDataDetails = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    const response = await getConsumptionDetails({
      finalDate,
      initialDate,
    });
    setConsumptioDetailsData(response);
    setIsLoading(false);
  }, [initialDate, finalDate]);

  useEffect(() => {
    if (typeReport === 'graph') {
      getGraphicData();
    } else {
      getGraphicDataDetails();
    }
  }, [getGraphicData, getGraphicDataDetails, typeReport]);

  const consumptionDataFormated = useMemo(() => {
    const translateType = monthType ? 'month' : 'day';
    return consumptionData.map((consumption) => {
      return {
        data: translateMonthAndYear(
          consumption.emissionDate,
          translateType,
          isMobile
        ),
        value: consumption.quantity,
        valueStatus: consumption.status,
      };
    });
  }, [consumptionData, monthType, isMobile]);

  const average = useMemo(() => {
    if (consumptionData?.length === 0) return 0;
    const total = consumptionData.reduce((acumulate, consumption) => {
      return acumulate + consumption.quantity;
    }, 0);
    return (total / consumptionData.length).toFixed(2);
  }, [consumptionData]);

  const maxValue = useMemo(() => {
    if (consumptionData?.length === 0) return 0;
    let newValue = 0;
    for (let i = 0; i < consumptionData.length; i += 1) {
      if (newValue < consumptionData[i].quantity) {
        newValue = consumptionData[i].quantity;
      }
    }
    return newValue;
  }, [consumptionData]);

  return {
    consumptionDetailsData,
    consumptionDataFormated,
    isLoading,
    isLoadingExcel,
    average,
    maxValue,
    exportExcel,
  };
};

export default useConsumptionHistory;
